import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import {
  DropdownToggleable,
  InputNumberToggleable,
} from '../../../../../components/inputs'
import { DelForProcessBodyTemplate } from '../../../../../components/tableTemplates'
import {
  DeliveryForecastProcessCompletions,
  EditSupplierFormDto,
} from '../../data'
import { SupplierFormFieldName } from '../Supplier.data'

type Props = {
  isEdit: boolean
  isRequestRunning: boolean
  isLoading: boolean
  formik: FormikProps<EditSupplierFormDto>
  isFormFieldInvalid: (name: SupplierFormFieldName) => boolean
  getFormErrorMessage: (name: SupplierFormFieldName) => JSX.Element
}

export const WebEdiDeliveryForecastSettings: React.FC<Props> = ({
  isEdit,
  isRequestRunning,
  isLoading,
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const isProcessActiveOptions: { name: string; code: boolean }[] = [
    {
      name: t(
        'supplier.webEdiDeliveryForecastSettings.form.activateDeliveryForecasts.options.active',
      ),
      code: true,
    },
    {
      name: t(
        'supplier.webEdiDeliveryForecastSettings.form.activateDeliveryForecasts.options.deactivated',
      ),
      code: false,
    },
  ]

  const deliveryForecastProcessCompletionsValueOptions: DeliveryForecastProcessCompletions[] =
    ['confirmation', 'despatchadvice', 'despatchadvice_with_confirmation']

  const delForProcessTemplate = (
    option: DeliveryForecastProcessCompletions,
  ) => {
    return DelForProcessBodyTemplate(option)
  }

  return (
    <PanelContainer
      header={t('supplier.webEdiDeliveryForecastSettings.title')}
      id="supplier-webEdiDeliveryForecastSettings"
      toggleable={false}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <p>{t('supplier.webEdiDeliveryForecastSettings.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <div className="formgrid grid">
              {/* ForecastIsProcessActive */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="deliveryForecastIsProcessActiveValue"
                >
                  {t(
                    'supplier.webEdiDeliveryForecastSettings.form.activateDeliveryForecasts.label',
                  )}
                </label>

                <DropdownToggleable
                  checked={
                    formik.values.deliveryForecastIsProcessActiveOverride
                  }
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'deliveryForecastIsProcessActiveOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiDeliveryForecastSettings.form.activateDeliveryForecasts.checkboxTooltip',
                  )}
                  id="deliveryForecastIsProcessActiveValue"
                  options={isProcessActiveOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.deliveryForecastIsProcessActiveValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'deliveryForecastIsProcessActiveValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* processCompletions */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="deliveryForecastProcessCompletionsValue"
                >
                  {t(
                    'supplier.webEdiDeliveryForecastSettings.form.processCompletions.label',
                  )}
                </label>

                <DropdownToggleable
                  checked={
                    formik.values.deliveryForecastProcessCompletionsOverride
                  }
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'deliveryForecastProcessCompletionsOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiDeliveryForecastSettings.form.activateDeliveryForecasts.checkboxTooltip',
                  )}
                  id="deliveryForecastProcessCompletionsValue"
                  options={deliveryForecastProcessCompletionsValueOptions}
                  value={formik.values.deliveryForecastProcessCompletionsValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'deliveryForecastProcessCompletionsValue',
                    ),
                  })}
                  valueTemplate={delForProcessTemplate}
                  itemTemplate={delForProcessTemplate}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* ForecastResponsePeriod */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid(
                      'deliveryForecastResponsePeriodValue',
                    ),
                  })}
                  htmlFor="deliveryForecastResponsePeriodValue"
                >
                  {t('supplier.webEdiOrderSettings.form.responsePeriod.label')}
                </label>
                <InputNumberToggleable
                  checked={formik.values.deliveryForecastResponsePeriodOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'deliveryForecastResponsePeriodOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.emailNotifications.form.language.checkboxTooltip',
                  )}
                  id="deliveryForecastResponsePeriodValue"
                  value={formik.values.deliveryForecastResponsePeriodValue}
                  onValueChange={formik.handleChange}
                  min={1}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'deliveryForecastResponsePeriodValue',
                    ),
                  })}
                  suffix={t('common.suffix.days', {
                    count: formik.values.deliveryForecastResponsePeriodValue,
                  })}
                  showButtons
                  locale={language}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PanelContainer>
  )
}
